import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Users, ChevronDown, ChevronUp, Link2 } from 'lucide-react'
import LoadingSpinner from '../../LoadingSpinner'

const parseNotes = (notes) => {
  if (!notes) return null
  try {
    return JSON.parse(notes)
  } catch {
    return null
  }
}

const FormDataDisplay = ({ notes }) => {
  const [expanded, setExpanded] = useState(false)
  const data = parseNotes(notes)
  if (!data) return null

  const transactionId = data.transactionId || data.transactionID || data.txnId
  const hasContent =
    data.jobTitle ||
    data.organization ||
    data.primaryGoals?.length ||
    data.heardAbout ||
    data.phone ||
    transactionId ||
    data.age ||
    data.gender ||
    data.company ||
    data.sessionSelection ||
    data.slotSelection ||
    data.facilitatorQueries ||
    data.childGender ||
    data.childAge ||
    data.aboutYourself ||
    data.sessionHope ||
    data.questions

  if (!hasContent) return null

  return (
    <div className="mt-2">
      <button
        onClick={() => setExpanded(!expanded)}
        className="inline-flex items-center gap-1 text-xs text-purple-300 hover:text-purple-200 transition-colors"
      >
        {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        {expanded ? 'Hide form responses' : 'View form responses'}
      </button>
      {expanded && (
        <div className="mt-2 p-3 bg-white/5 rounded-lg border border-white/10 space-y-1.5 text-xs">
          {data.phone && (
            <div><span className="text-gray-500">Phone:</span> <span className="text-gray-300">{data.phone}</span></div>
          )}
          {data.jobTitle && (
            <div><span className="text-gray-500">Job Title:</span> <span className="text-gray-300">{data.jobTitle}</span></div>
          )}
          {data.organization && (
            <div><span className="text-gray-500">Organization:</span> <span className="text-gray-300">{data.organization}</span></div>
          )}
          {data.primaryGoals?.length > 0 && (
            <div>
              <span className="text-gray-500">Goals:</span>
              <ul className="mt-0.5 ml-3 space-y-0.5">
                {data.primaryGoals.map((goal, i) => (
                  <li key={i} className="text-gray-300">- {goal}</li>
                ))}
              </ul>
            </div>
          )}
          {data.heardAbout && (
            <div><span className="text-gray-500">Heard about us:</span> <span className="text-gray-300">{data.heardAbout}</span></div>
          )}
          {transactionId && (
            <div><span className="text-gray-500">Transaction ID:</span> <span className="text-gray-300">{transactionId}</span></div>
          )}
          {data.age && (
            <div><span className="text-gray-500">Age:</span> <span className="text-gray-300">{data.age}</span></div>
          )}
          {data.gender && (
            <div><span className="text-gray-500">Gender:</span> <span className="text-gray-300">{data.gender}</span></div>
          )}
          {data.company && (
            <div><span className="text-gray-500">Company:</span> <span className="text-gray-300">{data.company}</span></div>
          )}
          {data.sessionSelection && (
            <div><span className="text-gray-500">Session selection:</span> <span className="text-gray-300">{data.sessionSelection}</span></div>
          )}
          {data.slotSelection && (
            <div><span className="text-gray-500">Slot selection:</span> <span className="text-gray-300">{data.slotSelection}</span></div>
          )}
          {data.facilitatorQueries && (
            <div><span className="text-gray-500">Queries:</span> <span className="text-gray-300">{data.facilitatorQueries}</span></div>
          )}
          {data.childGender && (
            <div><span className="text-gray-500">Child gender:</span> <span className="text-gray-300">{data.childGender}</span></div>
          )}
          {data.childAge && (
            <div><span className="text-gray-500">Child age:</span> <span className="text-gray-300">{data.childAge}</span></div>
          )}
          {data.aboutYourself && (
            <div><span className="text-gray-500">About registrant:</span> <span className="text-gray-300 whitespace-pre-wrap">{data.aboutYourself}</span></div>
          )}
          {data.sessionHope && (
            <div><span className="text-gray-500">Hope from session:</span> <span className="text-gray-300 whitespace-pre-wrap">{data.sessionHope}</span></div>
          )}
          {data.questions && (
            <div><span className="text-gray-500">Questions:</span> <span className="text-gray-300 whitespace-pre-wrap">{data.questions}</span></div>
          )}
        </div>
      )}
    </div>
  )
}

const getEventDetailPageUrl = (slug) => {
  if (!slug) return ''
  const path = `/events/${encodeURIComponent(slug)}`
  if (typeof window === 'undefined') return path
  return `${window.location.origin}${path}`
}

const EventRegistrationsModal = ({
  show,
  onClose,
  event,
  registrations,
  loading,
}) => {
  const [linkCopied, setLinkCopied] = useState(false)

  useEffect(() => {
    if (!show) setLinkCopied(false)
  }, [show])

  const handleCopyEventPageLink = async () => {
    if (!event?.slug) return
    const url = getEventDetailPageUrl(event.slug)
    try {
      await navigator.clipboard.writeText(url)
      setLinkCopied(true)
      setTimeout(() => setLinkCopied(false), 2000)
    } catch {
      alert('Could not copy link. Copy manually: ' + url)
    }
  }

  return createPortal(
    <AnimatePresence>
      {show && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={onClose}
          />

          <motion.div
            className="relative bg-slate-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden border border-white/20"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
          >
            <div className="flex items-start justify-between gap-3 px-6 py-4 border-b border-white/10">
              <div className="min-w-0 flex-1">
                <h2 className="text-xl font-bold text-white">Event Registrations</h2>
                {event && (
                  <>
                    <p className="text-sm text-gray-400">{event.name}</p>
                    {event.slug && (
                      <button
                        type="button"
                        onClick={handleCopyEventPageLink}
                        className="mt-2 inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-purple-200 bg-purple-500/15 border border-purple-400/30 hover:bg-purple-500/25 transition-colors w-full sm:w-auto justify-center sm:justify-start"
                      >
                        <Link2 className="w-4 h-4 flex-shrink-0" aria-hidden />
                        {linkCopied ? 'Copied!' : 'Copy event page link'}
                      </button>
                    )}
                  </>
                )}
              </div>
              <button
                type="button"
                onClick={onClose}
                className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]">
              {loading ? (
                <div className="flex justify-center py-12">
                  <LoadingSpinner />
                </div>
              ) : registrations.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                  <p className="text-gray-400">No registrations yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {registrations.map((reg, index) => (
                    <div key={reg.id} className="p-4 bg-white/5 rounded-xl">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-purple-500/20 text-purple-300 rounded-full flex items-center justify-center font-semibold flex-shrink-0">
                            {index + 1}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-white">{reg.user.name}</p>
                              {reg.isGuest && (
                                <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-500/20 text-amber-300">
                                  Guest
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-400">{reg.user.email}</p>
                            {reg.user.phone && <p className="text-sm text-gray-400">{reg.user.phone}</p>}
                          </div>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold flex-shrink-0 ${
                          reg.status === 'registered' ? 'bg-green-500/20 text-green-300' :
                          reg.status === 'attended' ? 'bg-blue-500/20 text-blue-300' :
                          'bg-gray-500/20 text-gray-300'
                        }`}>
                          {reg.status}
                        </span>
                      </div>
                      <FormDataDisplay notes={reg.notes} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  )
}

export default EventRegistrationsModal
